/** Multi-column SEO footer with categorized link groups. Data-driven via FOOTER_COLUMNS. */

import { Link } from "@tanstack/react-router";
import { Github, Mail, Twitter } from "lucide-react";
import { FOOTER_COLUMNS, type FooterColumn, SOCIAL_LINKS } from "@/constants/footer-links";
import logoPng from "@/logo.png";

const socialIcons = { github: Github, twitter: Twitter } as const;

function FooterColumnSection({ heading, headingTo, links }: FooterColumn) {
	return (
		<div>
			<h3 className="text-sm font-semibold text-foreground">
				{headingTo ? (
					<Link to={headingTo} className="hover:text-foreground/80 transition-colors">
						{heading}
					</Link>
				) : (
					heading
				)}
			</h3>
			<ul className="mt-3 flex flex-col gap-2">
				{links.map((link) => (
					<li key={link.to}>
						<Link
							to={link.to}
							className="text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							{link.label}
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

export function PublicFooter() {
	return (
		<footer className="surface-glass surface-divider w-full border-t">
			<div className="mx-auto max-w-6xl px-4 py-10 sm:py-12">
				{/* Link columns */}
				<nav aria-label="Footer navigation" className="grid grid-cols-2 gap-8 sm:grid-cols-3">
					{FOOTER_COLUMNS.map((col) => (
						<FooterColumnSection key={col.heading} {...col} />
					))}
				</nav>

				{/* Brand bar */}
				<div className="mt-10 flex flex-col items-center gap-4 border-t border-border/40 pt-6">
					<div className="flex w-full flex-col items-center gap-4 sm:flex-row sm:justify-between">
						<Link to="/">
							<img
								src={logoPng}
								alt="m8tes.ai"
								className="h-20 w-auto shrink-0 opacity-90 dark:invert"
							/>
						</Link>
						<div className="flex items-center gap-3">
							{SOCIAL_LINKS.map(({ href, label, icon }) => {
								const Icon = socialIcons[icon];
								return (
									<a
										key={href}
										href={href}
										target="_blank"
										rel="noopener noreferrer"
										className="text-muted-foreground hover:text-foreground transition-colors"
										aria-label={label}
									>
										<Icon className="size-5" />
									</a>
								);
							})}
						</div>
					</div>
					<div className="flex flex-col items-center gap-2 text-sm text-muted-foreground sm:flex-row sm:gap-3">
						<a
							href="mailto:sales@m8tes.ai"
							className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
						>
							<Mail className="size-4" />
							sales@m8tes.ai
						</a>
						<span className="hidden sm:inline" aria-hidden="true">
							·
						</span>
						<p suppressHydrationWarning>© {new Date().getFullYear()} m8tes. All rights reserved.</p>
					</div>
				</div>
			</div>
		</footer>
	);
}
